<article class="ts-page">
  <header class="ts-page__head">
    <app-spot kind="feather" class="ts-page__spot"></app-spot>
    <p class="t-meta ts-page__eyebrow">Pricing</p>
    <h1 class="ts-page__title">Pay for words, <em>not months</em></h1>
    <p class="ts-page__lede">
      There's no subscription. You buy Quills, spend them when the AI writes for you,
      and always see the price before you press.
    </p>
  </header>

  <dl class="ts-facts">
    <div class="ts-fact">
      <dt class="t-meta">One Quill</dt>
      <dd><span class="ts-fact__figure">€0.01</span>100 Quills is one euro</dd>
    </div>
    <div class="ts-fact">
      <dt class="t-meta">On sign-up</dt>
      <dd><span class="ts-fact__figure">Free</span>Quills to start writing</dd>
    </div>
    <div class="ts-fact">
      <dt class="t-meta">Expiry</dt>
      <dd><span class="ts-fact__figure">Never</span>Use them when you like</dd>
    </div>
    <div class="ts-fact">
      <dt class="t-meta">Subscription</dt>
      <dd><span class="ts-fact__figure">None</span>No monthly fee</dd>
    </div>
  </dl>

  <!-- ——— A worked example ——— -->
  <section class="ts-block ts-example" aria-labelledby="ts-example-title" tsReveal>
    <div class="ts-block__lead">
      <p class="t-meta ts-block__eyebrow">§ An example</p>
      <h2 class="ts-block__title" id="ts-example-title">What a book costs</h2>
      <p>
        Here is a ten-chapter book, written from start to finish. Setting it up took a handful of Quills.
        Each chapter cost a little more than the one before, because the AI reads more of the story every time.
      </p>
      <div class="ts-example__total-row">
        <p class="ts-example__total">
          <span class="ts-example__total-figure">{{ exampleTotal }} Quills</span>
          for the whole book: about {{ exampleTotal / 100 | number: '1.2-2' }} euro.
        </p>
        <app-doodle kind="hoard" class="ts-example__hoard"></app-doodle>
      </div>
      <p class="ts-block__note">
        Measured with Claude 3.5 Sonnet, one of the models we offered at the time. Today's models
        cost more or less than this, depending on which one you pick.
      </p>
    </div>

    <figure class="ts-receipt">
      <figcaption class="ts-receipt__head">
        <span class="t-meta">Quills spent</span>
        <span class="t-meta">Words read</span>
      </figcaption>
      <ol class="ts-receipt__rows">
        @for (row of exampleRows; track row.action; let i = $index) {
          <li class="ts-receipt__row" [class.is-setup]="row.inputWords === null">
            <span class="ts-receipt__label">{{ row.action }}</span>
            <span class="ts-receipt__track">
              <span class="ts-receipt__bar" [style.width]="barWidth(row)" [style.--i]="i"></span>
              <span class="ts-receipt__quills">{{ row.quills }}</span>
            </span>
            <span class="ts-receipt__words">{{ row.inputWords === null ? '—' : (row.inputWords | number) }}</span>
          </li>
        }
      </ol>
      <p class="ts-receipt__total">
        <span>Total</span>
        <span>{{ exampleTotal }} Quills</span>
      </p>
    </figure>
  </section>

  <!-- ——— How a price is made ——— -->
  <section class="ts-block ts-recipe-block" aria-labelledby="ts-recipe-title" tsReveal>
    <div class="ts-block__head">
      <p class="t-meta ts-block__eyebrow">§ The sum</p>
      <h2 class="ts-block__title" id="ts-recipe-title">How a price is made</h2>
    </div>

    <ol class="ts-recipe">
      <li class="ts-recipe__part">
        <h3>Words read</h3>
        <p>Everything the AI needs to see first: your style, your characters, the background and the chapters so far.</p>
      </li>
      <li class="ts-recipe__op" aria-hidden="true">+</li>
      <li class="ts-recipe__part">
        <h3>Words written</h3>
        <p>The text the AI writes for you: a chapter, a rewrite, a character profile.</p>
      </li>
      <li class="ts-recipe__op" aria-hidden="true">×</li>
      <li class="ts-recipe__part">
        <h3>The model's rate</h3>
        <p>Each model has its own rate, linked to what its provider charges. Switch to a cheaper one whenever you like.</p>
      </li>
    </ol>

    <aside class="ts-promise">
      <p class="t-meta ts-promise__label">Before you press</p>
      <p class="ts-promise__text">
        Every generation shows an estimate first. The final cost can come in lower,
        but never above the top of the estimate.
      </p>
      <p class="ts-promise__aside">You can check your balance and your usage history at any time.</p>
    </aside>
  </section>

  <!-- ——— Questions ——— -->
  <section class="ts-block ts-faq-block" aria-labelledby="ts-faq-title" tsReveal>
    <div class="ts-block__head">
      <p class="t-meta ts-block__eyebrow">§ Questions</p>
      <h2 class="ts-block__title" id="ts-faq-title">Asked before</h2>
    </div>

    <div class="ts-faq">
      @for (item of questions; track item.question) {
        <details class="ts-faq__item">
          <summary class="ts-faq__question">
            {{ item.question }}
            <span class="ts-faq__toggle" aria-hidden="true"></span>
          </summary>
          <p class="ts-faq__answer">{{ item.answer }}</p>
        </details>
      }
    </div>
  </section>

  <section class="ts-cta" aria-labelledby="ts-pricing-cta" tsReveal>
    <h2 class="ts-cta__title" id="ts-pricing-cta">Start with the Quills we give you.</h2>
    <div class="ts-cta__actions">
      @if (userIsLoggedIn) {
        <a class="ts-btn ts-btn--primary ts-btn--lg" [routerLink]="['/dashboard']">
          Open your dashboard <span class="ts-btn__arrow" aria-hidden="true">→</span>
        </a>
      } @else {
        <a class="ts-btn ts-btn--primary ts-btn--lg" [routerLink]="['/account/auth/signup']">
          Create your free account <span class="ts-btn__arrow" aria-hidden="true">→</span>
        </a>
      }
      <a class="ts-btn ts-btn--ghost ts-btn--lg" [routerLink]="['/features']">See what it can do</a>
    </div>
  </section>
</article>
